import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';

import { stepIDs, stepNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import CustomSteps from './custom-steps/CustomSteps';
import { Loader } from 'components';
import { Box, Typography } from '@mui/material';

const getTunnelStepName = (tunnelStep, profile) => {
    switch (tunnelStep.step.id) {
        case stepIDs.KYC_SURVEY:
            return profile === 'PP' ? texts.KYC_SURVEY : texts.INVESTOR_SURVEY_PM;
        case stepIDs.SURVEY_RESULTS:
            return tunnelStep.title || tunnelStep.step.name;
        default:
            return stepNames[tunnelStep.step.id];
    }
};

const TunnelPreview = ({
    loading,
    previewOnly,
    isFCPR,
    pendingPPTunnel,
    pendingPMTunnel,
    ppTunnelSteps,
    pmTunnelSteps,
    surveyStepTitle,
}) => {
    const ppTunnelPhaseId = useMemo(
        () => (pendingPPTunnel ? pendingPPTunnel.tunnelPhases[0].id : undefined),
        [pendingPPTunnel]
    );

    const pmTunnelPhaseId = useMemo(
        () => (pendingPMTunnel ? pendingPMTunnel.tunnelPhases[0].id : undefined),
        [pendingPMTunnel]
    );

    const ppTunnelGapStartIndex = useMemo(() => {
        let stepId = isFCPR ? stepIDs.KYC_PEDAGOGIC_STEP : stepIDs.ELIGIBILITY;
        return ppTunnelSteps ? ppTunnelSteps.findIndex(ts => ts.step.id === stepId) : -1;
    }, [isFCPR, ppTunnelSteps]);

    const pmTunnelGapStartIndex = useMemo(() => {
        let stepId = isFCPR ? stepIDs.KYC_PEDAGOGIC_STEP : stepIDs.ELIGIBILITY;
        return pmTunnelSteps ? pmTunnelSteps.findIndex(ts => ts.step.id === stepId) : -1;
    }, [isFCPR, pmTunnelSteps]);

    const ppStepsBeforeGap = useMemo(
        () => ppTunnelSteps?.slice(0, ppTunnelGapStartIndex + 1) || [],
        [ppTunnelSteps, ppTunnelGapStartIndex]
    );
    const ppStepsAfterGap = useMemo(() => {
        const tunnelSteps = [
            ...(ppTunnelSteps
                ?.slice(ppTunnelGapStartIndex + 1)
                ?.filter(ts => ts.mandatory && ts.step.id !== stepIDs.CUSTOM_STEP) || []),
        ];
        if (isFCPR && !tunnelSteps.find(ts => ts.step.id === stepIDs.SURVEY_RESULTS)) {
            let surveyStepIndex = tunnelSteps.findIndex(ts => ts.step.id === stepIDs.KYC_SURVEY);
            tunnelSteps.splice(surveyStepIndex + 1, 0, {
                id: Date.now(),
                mandatory: true,
                step: {
                    id: stepIDs.SURVEY_RESULTS,
                    name: surveyStepTitle || stepNames[stepIDs.SURVEY_RESULTS],
                },
            });
        }
        return tunnelSteps;
    }, [ppTunnelSteps, ppTunnelGapStartIndex, isFCPR, surveyStepTitle]);
    const pmStepsBeforeGap = useMemo(
        () => pmTunnelSteps?.slice(0, pmTunnelGapStartIndex + 1) || [],
        [pmTunnelSteps, pmTunnelGapStartIndex]
    );
    const pmStepsAfterGap = useMemo(() => {
        const tunnelSteps = [
            ...(pmTunnelSteps
                ?.slice(pmTunnelGapStartIndex + 1)
                ?.filter(ts => ts.mandatory && ts.step.id !== stepIDs.CUSTOM_STEP) || []),
        ];
        if (isFCPR && !tunnelSteps.find(ts => ts.step.id === stepIDs.SURVEY_RESULTS)) {
            let surveyStepIndex = tunnelSteps.findIndex(ts => ts.step.id === stepIDs.KYC_SURVEY);
            tunnelSteps.splice(surveyStepIndex + 1, 0, {
                id: Date.now(),
                mandatory: true,
                step: {
                    id: stepIDs.SURVEY_RESULTS,
                    name: surveyStepTitle || stepNames[stepIDs.SURVEY_RESULTS],
                },
            });
        }
        return tunnelSteps;
    }, [pmTunnelSteps, pmTunnelGapStartIndex, isFCPR, surveyStepTitle]);

    const ppCustomSteps = useMemo(() => {
        let pmCustomSteps = pmTunnelSteps.filter(ts => ts.step.id === stepIDs.CUSTOM_STEP);
        return ppTunnelSteps
            .filter(ts => ts.step.id === stepIDs.CUSTOM_STEP)
            .map((ts, index) => ({
                ...ts,
                pmId: pmCustomSteps[index].id,
                pmStepOrder: pmCustomSteps[index].order,
            }));
    }, [ppTunnelSteps, pmTunnelSteps]);

    return (
        <Box
            paddingX={2}
            paddingY={1}
            marginY={2}
            sx={{
                display: 'grid',
                gridTemplateColumns: '50% 50%',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '3px',
                gap: '12px',
            }}
        >
            <Loader loading={loading}>
                <Box>
                    <Typography fontWeight="600" sx={{ mb: 3 }} align="center">
                        {texts.PERSONNE_PHYSIQUE}
                    </Typography>
                    <Box>
                        {ppStepsBeforeGap.map(ts => (
                            <Typography gutterBottom fontSize="13px" key={ts.id}>
                                {getTunnelStepName(ts, 'PP')}
                            </Typography>
                        ))}
                    </Box>
                </Box>
                <Box>
                    <Typography fontWeight="600" sx={{ mb: 3 }} align="center">
                        {texts.PERSONNE_MORALE}
                    </Typography>
                    <Box>
                        {pmStepsBeforeGap.map(ts => (
                            <Typography gutterBottom fontSize="13px" key={ts.id}>
                                {getTunnelStepName(ts, 'PM')}
                            </Typography>
                        ))}
                    </Box>
                </Box>
                {previewOnly && (
                    <Box
                        sx={theme => ({
                            borderTop: `2px solid ${theme.palette.primary.main}`,
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            width: '80%',
                            gridColumn: '1 / span 2',
                            marginX: 'auto',
                            paddingY: 1,
                        })}
                    >
                        <Typography align="center">{texts.QUESTIONNAIRE}</Typography>
                    </Box>
                )}
                {!!pendingPPTunnel?.id && !previewOnly && (
                    <CustomSteps
                        ppTunnelPhaseId={ppTunnelPhaseId}
                        pmTunnelPhaseId={pmTunnelPhaseId}
                        customSteps={ppCustomSteps}
                        ppStepOrder={
                            ppCustomSteps.length
                                ? ppCustomSteps[ppCustomSteps.length - 1].order
                                : ppStepsBeforeGap?.[ppStepsBeforeGap?.length - 1]?.order
                        }
                        pmStepOrder={
                            ppCustomSteps.length
                                ? ppCustomSteps[ppCustomSteps.length - 1].order
                                : pmStepsBeforeGap?.[pmStepsBeforeGap?.length - 1]?.order
                        }
                    />
                )}
                <Box>
                    {ppStepsAfterGap.map(ts => (
                        <Typography gutterBottom fontSize="13px" key={ts.id}>
                            {getTunnelStepName(ts, 'PP')}
                        </Typography>
                    ))}
                </Box>
                <Box>
                    {pmStepsAfterGap.map(ts => (
                        <Typography gutterBottom fontSize="13px" key={ts.id}>
                            {getTunnelStepName(ts, 'PM')}
                        </Typography>
                    ))}
                </Box>
            </Loader>
        </Box>
    );
};

TunnelPreview.propTypes = {
    loading: PropTypes.bool,
    previewOnly: PropTypes.bool,
    isFCPR: PropTypes.bool,
    pendingPPTunnel: PropTypes.object,
    pendingPMTunnel: PropTypes.object,
    ppTunnelSteps: PropTypes.array,
    pmTunnelSteps: PropTypes.array,
    surveyStepTitle: PropTypes.string,
};

export default TunnelPreview;
